import Box from '@/common/components/Box'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import ResumeItem from '@/common/components/ResumeItem'
import { formatCurrency, formatUnit } from '@/common/utils/formatters'
import ProposalAwareResumeItem from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/ExecutionEntryCompleted/ProposalAwareResumeItem'
import ProposalGroupHeader from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/ExecutionEntryCompleted/ProposalGroupHeader'
import { isExecutionEntryProposalContent } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/establishmentChoiceEntries/proposalContents/proposalContent.type'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import { MaintenanceForwardingEstablishmentChoiceEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/establishmentChoiceEntries/establishmentChoiceEntry.type'
import { MaintenanceDirectiveObjectiveData } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import { WrenchIcon } from 'lucide-react'
import { useMemo } from 'react'

interface ExecutionEntryObjectiveActivitiesProps {
  objective: MaintenanceDirectiveObjectiveData
  dataUpdatedAt?: number
}

const ExecutionEntryObjectiveActivities = ({ objective, dataUpdatedAt }: ExecutionEntryObjectiveActivitiesProps) => {
  const { forwarding } = useForwardingTabContext<
    MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry,
    MaintenanceForwardingEstablishmentChoiceEntryKlass.ManualEntry
  >()

  const proposalStatus = forwarding?.establishment_choice_entry?.actual_proposal?.status
  const proposalContent = forwarding?.establishment_choice_entry?.actual_proposal?.proposal_content

  const activityModifications = isExecutionEntryProposalContent(proposalContent, proposalStatus)
    ? proposalContent.activity_modifications
    : undefined

  const modificationByActivityId = useMemo(() => {
    const map = new Map<string, NonNullable<typeof activityModifications>[number]>()

    activityModifications?.forEach((modification) => {
      map.set(modification.objective_activity_id, modification)
    })

    return map
  }, [activityModifications])

  const activitiesCountDisplay = useMemo(() => {
    const count = objective.execution_activities?.length || 0

    if (count === 0) {
      return 'Nenhum serviço'
    }

    if (count === 1) {
      return '1 Serviço'
    }

    return `${count} Serviços`
  }, [dataUpdatedAt])

  const totalValueSum = useMemo(() => {
    return (
      objective.execution_activities?.reduce((prev, curr) => {
        const modification = modificationByActivityId.get(curr.id)
        const value = modification?.new_total_value ?? curr.total_value
        return prev + Number(value)
      }, 0) || 0
    )
  }, [dataUpdatedAt, modificationByActivityId])

  const originalValueSum = useMemo(() => {
    return (
      objective.execution_activities?.reduce((prev, curr) => {
        const modification = modificationByActivityId.get(curr.id)
        const value = modification?.original_total_value ?? curr.total_value
        return prev + Number(value)
      }, 0) || 0
    )
  }, [dataUpdatedAt, modificationByActivityId])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <ProposalGroupHeader
          title="Serviços"
          countDisplay={activitiesCountDisplay}
          totalValueSum={totalValueSum}
          originalValueSum={originalValueSum}
        />
      </div>

      <div className="flex flex-col gap-3">
        {objective.execution_activities?.map((diagnosticActivity) => {
          const modification = modificationByActivityId.get(diagnosticActivity.id)

          return (
            <Box key={diagnosticActivity.id} className="p-4">
              <div className="flex gap-4">
                <ImageCarousel
                  files={
                    diagnosticActivity.images?.map((image) => ({
                      id: image.id,
                      file: image.url,
                    })) || []
                  }
                  Placeholder={
                    <ImageWithPlaceholder
                      className="size-20 rounded-md"
                      iconClassName="size-10"
                      placeholderIcon={WrenchIcon}
                    />
                  }
                  className="size-20"
                  showExpandButton
                  showRemoveButton={false}
                />

                <div className="flex flex-col gap-2">
                  <p className="text-base font-bold">{diagnosticActivity?.display_name || '--'}</p>

                  <div className="flex items-center flex-wrap gap-3">
                    {diagnosticActivity.unit_for_quantity === 'minutes' ? (
                      <ProposalAwareResumeItem
                        label="Valor por hora"
                        originalValue={Number(diagnosticActivity?.unit_price) || 0}
                        newValue={modification?.new_unit_price != null ? Number(modification.new_unit_price) : null}
                        format={formatCurrency}
                      />
                    ) : (
                      <ProposalAwareResumeItem
                        label="Valor unt."
                        originalValue={Number(diagnosticActivity?.unit_price) || 0}
                        newValue={modification?.new_unit_price != null ? Number(modification.new_unit_price) : null}
                        format={formatCurrency}
                      />
                    )}

                    <p className="text-sm">x</p>

                    {diagnosticActivity.unit_for_quantity === 'minutes' ? (
                      <ResumeItem
                        className="text-sm"
                        label="Tempo de execução"
                        value={formatUnit(diagnosticActivity.quantity, 'hours')}
                      />
                    ) : (
                      <ResumeItem
                        className="text-sm"
                        label="Unidades"
                        value={formatUnit(diagnosticActivity.quantity, 'unit')}
                      />
                    )}

                    <p className="text-sm">=</p>

                    <ProposalAwareResumeItem
                      label="Valor total"
                      originalValue={Number(modification?.original_total_value ?? diagnosticActivity?.total_value) || 0}
                      newValue={modification?.new_total_value != null ? Number(modification.new_total_value) : null}
                      differencePercentage={modification?.difference_percentage}
                      format={formatCurrency}
                    />

                    <ProposalAwareResumeItem
                      label="Garantia"
                      originalValue={diagnosticActivity.warranty_in_days}
                      newValue={modification?.new_warranty_in_days}
                      format={(value) => `${value} Dias`}
                      higherIsBetter
                    />
                  </div>

                  <ResumeItem className="text-sm" label="Descrição" value={diagnosticActivity.description} />
                </div>
              </div>
            </Box>
          )
        })}
      </div>
    </div>
  )
}

export default ExecutionEntryObjectiveActivities
